/*
 * Copyright (c) 2025, WSO2 LLC (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React, { useState } from "react";

import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from "@material-ui/core";
import CloseIcon from '@material-ui/icons/Close';

import { useStyles } from "./style";
import { useIntl } from "react-intl";
import Settings from "../../../../conf/settings";
import EditableTextField from "../../../../elements/EditableTextField";

interface ConfirmationDialogProps {
    open: boolean,
    clientId: string,
    selectedIdp: any,
    idpRegistered: boolean,
    defaultAsgardeoKeyManagerName: string,
    dialogTitle: string,
    okayButtonLabel: string,
    cancelButtonLabel: string,
    onClose: () => void,
    onOkay: () => void,
    onCancel: () => void,
    onChange: (clientId: string) => void,
}

export const ClientIdConfirmationDialog = (props: ConfirmationDialogProps) => {
    const {
        open, clientId, selectedIdp, defaultAsgardeoKeyManagerName, dialogTitle, okayButtonLabel, cancelButtonLabel,
         onClose, onOkay, onCancel, onChange,
    } = props;


    const [currentClientId, setCurrentClientId] = useState(clientId);
    const [previousClientId, setPreviousClientId] = useState(clientId);
    const [updateEnabled, setUpdateEnabled] = useState(true);
    const classes = useStyles();
    const intl = useIntl();

    const handleInputChange = (value: string) => {
        setPreviousClientId(currentClientId);
        setCurrentClientId(value);
        onChange(value);
        setUpdateEnabled(false);
    };

    const handleCancel = () => {
        setCurrentClientId(previousClientId);
        onChange(previousClientId);
        onCancel();
    };

    const warningBanner = intl.formatMessage({
        id: "application.productionKeys.oAuth.tokenExchange.info",
        defaultMessage: "Modifying your Client ID may impact requests from existing clients.",
    });

    const DialogContents = () => {
      return (
        <div>
          <Box
            className={classes.warningContainer}
            data-testid="warninng-banner"
          >
            <Box display="flex" flexDirection="row">
              <Box
                display="flex"
                flexDirection="row"
                ml={1}
                data-testid={"warning-test"}
              >
                <Typography variant="body1" align="left">
                  {warningBanner}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box py={1.5}>
            <EditableTextField
              data-testid="client-id-input"
              isEditable
              key="clientId"
              placeholder={intl.formatMessage(
                {
                  id: "application.productionKeys.oAuth.enterClientId.placeholder",
                  defaultMessage: `Enter {selectedIdpName} client ID here..`,
                },
                {
                  selectedIdpName:
                    Settings.choreoInbuiltKeyManagerFeatureEnabled &&
                    selectedIdp?.name === defaultAsgardeoKeyManagerName
                      ? selectedIdp?.displayName
                      : selectedIdp?.name,
                }
              )}
              value={clientId}
              onAction={(clientId: string) => handleInputChange(clientId)}
            />
          </Box>
        </div>
      );
    };

    return (
        <Dialog
            open={open}
            onClose={handleCancel}
            aria-labelledby="confirmation-dialog"
            fullWidth={true}
            maxWidth="xs"
            classes={{paper: classes.dialogRootStyle}}
        >
            <DialogTitle>
                {
                    handleCancel ? (
                        <IconButton aria-label="close" className={classes.closeButton} onClick={handleCancel}>
                            <CloseIcon />
                        </IconButton>
                    ) : null
                }
            </DialogTitle>
            <DialogContent className={classes.dialogContent}>
                <Typography
                    data-testid={dialogTitle}
                    variant="h1"
                    component="div"
                    className={classes.primaryTitle}
                >
                    {dialogTitle}
                </Typography>
                <div className={classes.dialogContent}>
                    <DialogContents />
                </div>
            </DialogContent>
            <DialogActions className={classes.dialogActions}>
                <Button
                    variant="contained"
                    color="default"
                    classes={{ root: classes.dialogButton }}
                    onClick={handleCancel}
                    data-testid="clientId-cancel"
                >
                    {cancelButtonLabel}
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    classes={{ root: classes.dialogButton }}
                    onClick={(e) => {
                        e.preventDefault();
                        onOkay();
                    }}
                    data-testid="clientId-save"
                    disabled={updateEnabled}
                >
                    {okayButtonLabel}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
